import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API_BASE, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { naturalCompare } from "../lib/utils";
import { toast } from "sonner";
import {
  FileSpreadsheet,
  Upload,
  Download,
  FileDown,
  CheckCircle2,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Search,
  X,
  Receipt,
  ClipboardList,
  ChevronDown,
} from "lucide-react";
import axios from "axios";

/**
 * Excel import / export screen. Admin-only for import (writes to inventory);
 * everyone can export the (filtered) stock list. Import uses a two-step flow
 * — first a validation dry-run, then commit — so no partial writes occur if
 * any barcode collides with existing stock.
 */
export default function ExcelTransfer() {
  const { user } = useAuth();
  const isAdmin = ["admin", "supervisor"].includes(user?.role);
  const [tab, setTab] = useState("import");

  return (
    <div className="space-y-4">
      <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <FileSpreadsheet size={22} className="text-[color:var(--success)]" />
          <div className="ticker text-[color:var(--text-3)]">/// EXCEL AKTARIMI</div>
        </div>
        <div className="flex hard border-2 border-[color:var(--border)] overflow-hidden ml-4">
          <button
            data-testid="excel-tab-import"
            onClick={() => setTab("import")}
            className={`px-4 h-[42px] text-sm font-semibold uppercase tracking-wider ${
              tab === "import"
                ? "bg-[color:var(--primary)] text-white"
                : "bg-white hover:bg-[color:var(--bg)]"
            }`}
          >
            <Upload size={14} className="inline mr-1" /> İçe Aktar
          </button>
          <button
            data-testid="excel-tab-export"
            onClick={() => setTab("export")}
            className={`px-4 h-[42px] text-sm font-semibold uppercase tracking-wider ${
              tab === "export"
                ? "bg-[color:var(--primary)] text-white"
                : "bg-white hover:bg-[color:var(--bg)]"
            }`}
          >
            <ClipboardList size={14} className="inline mr-1" /> Sipariş Listesi
          </button>
        </div>
        <div className="ticker text-[color:var(--text-3)] ml-auto text-xs">
          Excel şablonu: İSİM · EBAT · BARKOD · ADET · SEHPA · TARİH · KALİTE · AÇIKLAMA
        </div>
      </div>

      {tab === "import" ? (
        <ImportPanel isAdmin={isAdmin} />
      ) : (
        <ExportPanel />
      )}
    </div>
  );
}

/* --------------------------------- Import --------------------------------- */

function ImportPanel({ isAdmin }) {
  const [file, setFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [report, setReport] = useState(null); // dry-run result
  const [committed, setCommitted] = useState(null);
  const fileInputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setReport(null);
    setCommitted(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/inventory/excel-template", { responseType: "blob" });
      saveBlob(res.data, "stok_sablonu.xlsx");
      toast.success("Şablon indirildi");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const runDryRun = async (chosen) => {
    setValidating(true);
    setReport(null);
    setCommitted(null);
    try {
      const fd = new FormData();
      fd.append("file", chosen);
      const res = await axios.post(
        `${API_BASE}/inventory/excel-import?dry_run=true`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
      );
      setReport(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setValidating(false);
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".xlsx")) {
      toast.error("Yalnızca .xlsx dosyaları desteklenir");
      return;
    }
    setFile(f);
    runDryRun(f);
  };

  const commit = async () => {
    if (!file || !report?.ok) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(
        `${API_BASE}/inventory/excel-import`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } },
      );
      setCommitted(res.data);
      toast.success(`${res.data.inserted} kayıt stoğa eklendi`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCommitting(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="hard border border-[color:var(--warning)] bg-[color:var(--warning-bg)] text-[color:var(--warning)] p-4">
        Excel içe aktarma yalnızca yöneticiler içindir. Dışa aktarma sekmesini kullanabilirsiniz.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="hard border-2 border-[color:var(--border)] bg-[color:var(--surface)] p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-lg font-black mb-1">Excel Dosyasından Stok Ekle</h2>
            <p className="text-sm text-[color:var(--text-2)] max-w-2xl">
              Adres defterindeki 8 kolonlu Excel formatını yükleyin. Kayıtlar önce
              doğrulanır; hata yoksa <b>Onayla & Yükle</b> butonu ile stoğa eklenir.
              Barkodlardan herhangi biri sistemde zaten varsa aktarım durdurulur —
              adet güncellemek için Toplu Giriş sekmesini kullanın.
            </p>
          </div>
          <button
            data-testid="excel-download-template"
            onClick={downloadTemplate}
            className="px-4 h-10 hard border-2 border-[color:var(--border)] hover:border-[color:var(--primary)] hover:text-[color:var(--primary)] text-sm font-bold uppercase tracking-wider flex items-center gap-2 transition-colors"
          >
            <FileDown size={14} /> Şablon İndir
          </button>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-[1fr_auto] items-center">
          <label
            htmlFor="excel-file-input"
            className="block hard border-2 border-dashed border-[color:var(--border)] hover:border-[color:var(--primary)] p-6 cursor-pointer transition-colors text-center"
          >
            <Upload size={22} className="mx-auto mb-2 text-[color:var(--text-3)]" />
            <div className="text-sm font-semibold">
              {file ? file.name : "Excel dosyasını seçmek için tıklayın"}
            </div>
            <div className="ticker text-[10px] text-[color:var(--text-3)] mt-1">
              yalnızca .xlsx
            </div>
            <input
              id="excel-file-input"
              ref={fileInputRef}
              type="file"
              accept=".xlsx"
              onChange={onFileChange}
              data-testid="excel-file-input"
              className="hidden"
            />
          </label>
          {file && (
            <button
              data-testid="excel-reset"
              onClick={reset}
              className="h-10 px-4 hard border-2 border-[color:var(--border)] hover:border-[color:var(--error)] hover:text-[color:var(--error)] text-sm font-bold uppercase tracking-wider flex items-center gap-1 transition-colors"
            >
              <RefreshCw size={14} /> Sıfırla
            </button>
          )}
        </div>
      </div>

      {validating && (
        <div className="hard border border-[color:var(--border)] bg-[color:var(--surface)] p-4 flex items-center gap-2 text-sm">
          <Loader2 size={16} className="animate-spin" />
          Dosya doğrulanıyor…
        </div>
      )}

      {report && !committed && (
        <ImportReport
          report={report}
          onCommit={commit}
          committing={committing}
        />
      )}

      {committed && (
        <div
          data-testid="excel-import-success"
          className="hard border-2 border-[color:var(--success)] bg-[color:var(--success-bg)] p-6"
        >
          <div className="flex items-center gap-2 text-[color:var(--success)]">
            <CheckCircle2 size={22} />
            <div className="font-black text-lg">Aktarım Tamamlandı</div>
          </div>
          <div className="text-sm mt-2">
            <b>{committed.inserted}</b> yeni kayıt stoğa eklendi. Fiş numarası:{" "}
            <span className="font-mono font-bold">{committed.fis_id}</span>
          </div>
          <button
            onClick={reset}
            className="mt-4 px-4 h-10 hard bg-[color:var(--primary)] text-white text-sm font-bold uppercase tracking-wider"
          >
            Yeni Dosya Yükle
          </button>
        </div>
      )}
    </div>
  );
}

function ImportReport({ report, onCommit, committing }) {
  const { ok, valid_count, error_count, errors = [], preview = [] } = report;
  return (
    <div className="hard border-2 border-[color:var(--border)] bg-[color:var(--surface)]">
      <div className="p-4 border-b border-[color:var(--border)] flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span
            data-testid="excel-report-status"
            className={`inline-flex items-center gap-1 px-3 py-1 hard font-bold uppercase text-xs tracking-wider ${
              ok
                ? "bg-[color:var(--success-bg)] text-[color:var(--success)]"
                : "bg-[color:var(--error-bg)] text-[color:var(--error)]"
            }`}
          >
            {ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
            {ok ? "Aktarıma hazır" : "Hata var — aktarım durduruldu"}
          </span>
          <div className="text-sm">
            <b className="font-mono">{valid_count}</b> geçerli ·{" "}
            <b className="font-mono text-[color:var(--error)]">{error_count}</b> hata
          </div>
        </div>
        {ok && (
          <button
            data-testid="excel-commit"
            onClick={onCommit}
            disabled={committing}
            className="px-5 h-10 hard bg-[color:var(--success)] hover:opacity-90 text-white font-bold uppercase tracking-wider text-sm flex items-center gap-2 disabled:opacity-50"
          >
            {committing ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Yükleniyor…
              </>
            ) : (
              <>
                <CheckCircle2 size={14} /> Onayla & Yükle ({valid_count})
              </>
            )}
          </button>
        )}
      </div>

      {errors.length > 0 && (
        <div className="p-4 border-b border-[color:var(--border)] bg-[color:var(--error-bg)]">
          <div className="ticker text-[10px] text-[color:var(--error)] mb-2">
            /// HATALAR
          </div>
          <ul
            data-testid="excel-report-errors"
            className="text-xs space-y-1 max-h-56 overflow-auto font-mono"
          >
            {errors.map((e, i) => (
              <li key={i} className="flex gap-2">
                <AlertTriangle size={12} className="mt-0.5 flex-shrink-0 text-[color:var(--error)]" />
                <span>{e}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {preview.length > 0 && (
        <div className="p-4">
          <div className="ticker text-[10px] text-[color:var(--text-3)] mb-2">
            /// EKLENECEK KAYITLAR ({preview.length})
          </div>
          <div className="overflow-auto max-h-80 hard border border-[color:var(--border)]">
            <table className="dense-table w-full">
              <thead>
                <tr>
                  <th>Barkod</th>
                  <th>İsim</th>
                  <th>Ebat</th>
                  <th className="text-right">Adet</th>
                  <th>Sehpa</th>
                  <th>Tarih</th>
                  <th>Kalite</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((r, i) => (
                  <tr key={r.barkod + i} data-testid="excel-preview-row">
                    <td className="font-mono font-semibold">{r.barkod}</td>
                    <td className="max-w-xs truncate">{r.isim || "-"}</td>
                    <td className="font-mono">{r.ebat || "-"}</td>
                    <td className="font-mono font-bold text-right">{r.adet}</td>
                    <td className="font-mono">{r.sehpa_konumu}</td>
                    <td className="font-mono text-xs">{r.gelis_tarihi || "-"}</td>
                    <td className="text-xs">{r.urun_kalitesi || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------------- Export --------------------------------- */

function ExportPanel() {
  const [firma, setFirma] = useState("");
  const [sehpa, setSehpa] = useState("");
  const [kalite, setKalite] = useState("");
  const [includeEmpty, setIncludeEmpty] = useState(false);
  const [isimQ, setIsimQ] = useState("");
  // Order slip metadata
  const [musteri, setMusteri] = useState("");
  const [siparisNotu, setSiparisNotu] = useState("");
  const [creatingSlip, setCreatingSlip] = useState(false);

  const [brands, setBrands] = useState([]);
  const [shelves, setShelves] = useState([]);
  const [facets, setFacets] = useState({ urun_kalitesi: [] });
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  // Selected items are stored as a Map<id, item> so the "sepet" persists
  // across filter/search changes — the user can pick from A-1, then change
  // to B-3, keep picking, and export the combined selection.
  const [selected, setSelected] = useState(() => new Map());
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/stats/brands/list").then(({ data }) => setBrands(data)).catch(() => {});
    api.get("/shelves").then(({ data }) => setShelves(data || [])).catch(() => {});
    api.get("/inventory/facets").then(({ data }) => setFacets(data)).catch(() => {});
  }, []);

  const filters = useMemo(
    () => ({ firma, sehpa, kalite, include_empty: includeEmpty, isim_q: isimQ }),
    [firma, sehpa, kalite, includeEmpty, isimQ],
  );

  // Load matching items (debounced on isim search). We cap at 500 rows to
  // keep the browser snappy; the count reflects the full filter result.
  useEffect(() => {
    setLoading(true);
    const params = {
      firma,
      sehpa,
      kalite,
      isim_q: isimQ,
      include_empty: includeEmpty,
      limit: 500,
      skip: 0,
    };
    const handle = setTimeout(() => {
      api
        .get("/inventory", { params })
        .then(({ data }) => {
          setItems(data.items || []);
          setTotal(data.total);
        })
        .catch(() => {
          setItems([]);
          setTotal(null);
        })
        .finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(handle);
  }, [firma, sehpa, kalite, includeEmpty, isimQ]);

  // Selection is INTENTIONALLY preserved across filter changes — the user
  // may pick from one sehpa, switch filter, and keep adding from another.

  const toggle = (item) =>
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(item.id)) next.delete(item.id);
      else next.set(item.id, item);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Map(prev);
      const allSelected =
        items.length > 0 && items.every((i) => next.has(i.id));
      if (allSelected) {
        for (const it of items) next.delete(it.id);
      } else {
        for (const it of items) next.set(it.id, it);
      }
      return next;
    });

  const removeFromSelection = (id) =>
    setSelected((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });

  const clearSelection = () => setSelected(new Map());

  const allVisibleSelected =
    items.length > 0 && items.every((i) => selected.has(i.id));
  const someSelected = selected.size > 0 && !allVisibleSelected;

  const download = async (mode) => {
    // mode: 'selected' | 'all'
    setDownloading(true);
    try {
      const body =
        mode === "selected" && selected.size > 0
          ? { ids: Array.from(selected.keys()) }
          : { filters };
      const res = await api.post(
        "/inventory/excel-export",
        body,
        { responseType: "blob" },
      );
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      saveBlob(res.data, `stok_export_${stamp}.xlsx`);
      const n = mode === "selected" ? selected.size : total ?? "?";
      toast.success(`Excel indirildi (${n} kayıt)`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDownloading(false);
    }
  };

  const createOrderSlip = async () => {
    if (selected.size === 0) return;
    setCreatingSlip(true);
    try {
      const res = await api.post(
        "/inventory/order-slip",
        {
          ids: Array.from(selected.keys()),
          musteri: musteri.trim(),
          aciklama: siparisNotu.trim(),
        },
        { responseType: "blob" },
      );
      const stamp = new Date().toISOString().slice(0, 16).replace(/[-:T]/g, "");
      const label = musteri.trim() || "musteri";
      saveBlob(res.data, `siparis_fisi_${label}_${stamp}.xlsx`);
      toast.success(`Sipariş fişi oluşturuldu (${selected.size} kalem)`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreatingSlip(false);
    }
  };

  const selectionSummary = useMemo(() => {
    return { count: selected.size };
  }, [selected]);

  // Aggregate selected items into shipment lines. Rows sharing the same
  // (konum + isim + ebat + gelis_tarihi) become ONE line where "blok" is the
  // number of picks. We DO NOT expose barkod or actual stock adet — otherwise
  // the crane team would treat each pick as a specific labelled block and be
  // forced to physically move that exact piece. Business rule: only the blok
  // count matters; any block of matching size/date at that address works.
  const orderLines = useMemo(() => {
    const map = new Map();
    for (const it of selected.values()) {
      const key = [
        it.sehpa_konumu || "-",
        it.isim || "-",
        it.ebat || "-",
        it.gelis_tarihi || "",
      ].join("|");
      const g = map.get(key) || {
        key,
        konum: it.sehpa_konumu || "-",
        isim: it.isim || "-",
        ebat: it.ebat || "-",
        gelis_tarihi: it.gelis_tarihi || "",
        kalite: it.urun_kalitesi || "-",
        blok: 0,
        ids: [],
      };
      g.blok += 1;
      g.ids.push(it.id);
      map.set(key, g);
    }
    return Array.from(map.values()).sort((a, b) => {
      const c = naturalCompare(a.konum, b.konum);
      if (c) return c;
      return (a.gelis_tarihi || "").localeCompare(b.gelis_tarihi || "");
    });
  }, [selected]);

  const removeGroup = (ids) =>
    setSelected((prev) => {
      const next = new Map(prev);
      for (const id of ids) next.delete(id);
      return next;
    });

  const totalBlok = orderLines.reduce((s, l) => s + l.blok, 0);

  // Group the sepet items by konum for a readable chip summary.
  const selectionByKonum = useMemo(() => {
    const groups = {};
    for (const g of orderLines) {
      if (!groups[g.konum]) groups[g.konum] = { blok: 0 };
      groups[g.konum].blok += g.blok;
    }
    return groups;
  }, [orderLines]);

  return (
    <div className="space-y-4">
      <IncomingOrderPanel
        onPickItem={(item) =>
          setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(item.id)) next.delete(item.id);
            else next.set(item.id, item);
            return next;
          })
        }
        selected={selected}
      />

      <div className="hard border-2 border-[color:var(--border)] bg-[color:var(--surface)] p-6">
        <h2 className="text-lg font-black mb-1">Sipariş Listesi Oluştur</h2>
        <p className="text-sm text-[color:var(--text-2)] mb-6">
          Cam ismi yazıp aramanızı daraltın, <b>tek tek işaretleyerek sipariş
          listenize ekleyin</b>. Filtreyi değiştirseniz bile seçtikleriniz
          kaybolmaz — farklı sehpalardan (örn. A-1, B-3, F-7) parça parça
          toplayıp <b>StarGrup Sipariş Fişi</b> olarak dışa aktarın. Sepet
          boşsa "Tümünü Aktar" tüm eşleşen stok listesini indirir.
        </p>

        <div className="mb-4 relative">
          <Search
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--text-3)]"
          />
          <input
            data-testid="export-isim-search"
            value={isimQ}
            onChange={(e) => setIsimQ(e.target.value)}
            placeholder="Cam ismi ara… (örn. Guardian 4mm)"
            className="w-full h-11 pl-9 pr-3 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none"
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <div>
            <label className="ticker text-[10px] text-[color:var(--text-3)] block mb-1">
              FİRMA
            </label>
            <select
              data-testid="export-firma"
              value={firma}
              onChange={(e) => setFirma(e.target.value)}
              className="w-full h-10 px-3 hard border-2 border-[color:var(--border)] bg-white text-sm"
            >
              <option value="">Tümü</option>
              {brands.map((b) => (
                <option key={b.firma} value={b.firma}>
                  {b.firma}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="ticker text-[10px] text-[color:var(--text-3)] block mb-1">
              SEHPA KONUMU
            </label>
            <select
              data-testid="export-sehpa"
              value={sehpa}
              onChange={(e) => setSehpa(e.target.value)}
              className="w-full h-10 px-3 hard border-2 border-[color:var(--border)] bg-white text-sm"
            >
              <option value="">Tümü</option>
              {shelves.map((s) => (
                <option key={s.sehpa} value={s.sehpa}>
                  {s.sehpa} ({s.product_count})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="ticker text-[10px] text-[color:var(--text-3)] block mb-1">
              ÜRÜN KALİTESİ
            </label>
            <select
              data-testid="export-kalite"
              value={kalite}
              onChange={(e) => setKalite(e.target.value)}
              className="w-full h-10 px-3 hard border-2 border-[color:var(--border)] bg-white text-sm"
            >
              <option value="">Tümü</option>
              {(facets.urun_kalitesi || []).map((k) => (
                <option key={k} value={k}>
                  {k}
                </option>
              ))}
            </select>
          </div>
        </div>

        <label className="flex items-center gap-2 mt-4 text-sm cursor-pointer select-none">
          <input
            type="checkbox"
            data-testid="export-include-empty"
            checked={includeEmpty}
            onChange={(e) => setIncludeEmpty(e.target.checked)}
            className="h-4 w-4"
          />
          Adeti 0 olan kayıtları dahil et
        </label>
      </div>

      {/* Selection table */}
      <div className="hard border-2 border-[color:var(--border)] bg-[color:var(--surface)]">
        <div className="p-4 border-b border-[color:var(--border)] flex flex-wrap items-center gap-3">
          <div className="text-sm">
            Eşleşen kayıt:{" "}
            <b
              data-testid="export-count"
              className="font-mono text-lg text-[color:var(--primary)]"
            >
              {loading ? "…" : (total ?? "-")}
            </b>
            {total != null && items.length < total && (
              <span className="ticker text-[10px] text-[color:var(--warning)] ml-2">
                (Seçim için ilk {items.length} kayıt gösteriliyor — filtreleri daraltın)
              </span>
            )}
          </div>
          <div className="text-sm ml-auto flex items-center gap-3">
            <span>
              Seçili:{" "}
              <b
                data-testid="export-selected-count"
                className="font-mono text-lg text-[color:var(--success)]"
              >
                {totalBlok}
              </b>
              <span className="ticker text-[10px] text-[color:var(--text-3)] ml-1">
                blok
              </span>
            </span>
            {selected.size > 0 && (
              <button
                data-testid="export-clear-selection"
                onClick={clearSelection}
                className="text-xs underline text-[color:var(--text-2)] hover:text-[color:var(--error)]"
              >
                temizle
              </button>
            )}
          </div>
        </div>

        <div className="max-h-[520px] overflow-auto">
          <table className="dense-table w-full">
            <thead className="sticky top-0 bg-[color:var(--surface)] z-10">
              <tr>
                <th className="w-10">
                  <input
                    type="checkbox"
                    data-testid="export-toggle-all"
                    checked={allVisibleSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected;
                    }}
                    onChange={toggleAll}
                    className="h-4 w-4"
                  />
                </th>
                <th>Barkod</th>
                <th>İsim</th>
                <th>Ebat</th>
                <th className="text-right">Adet</th>
                <th>Sehpa</th>
                <th>Kalite</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={7} className="text-center py-8 text-[color:var(--text-3)] text-sm">
                    <Loader2 size={14} className="inline animate-spin mr-2" /> Yükleniyor…
                  </td>
                </tr>
              )}
              {!loading && items.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    data-testid="export-empty"
                    className="text-center py-8 text-[color:var(--text-3)] text-sm"
                  >
                    Bu filtrelerle eşleşen cam bulunamadı.
                  </td>
                </tr>
              )}
              {!loading &&
                items.map((it) => {
                  const isSel = selected.has(it.id);
                  return (
                    <tr
                      key={it.id}
                      data-testid="export-row"
                      onClick={() => toggle(it)}
                      className={`cursor-pointer transition-colors ${
                        isSel ? "bg-[color:var(--success-bg)]" : "hover:bg-[color:var(--bg)]"
                      }`}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          data-testid={`export-row-check-${it.id}`}
                          checked={isSel}
                          onChange={() => toggle(it)}
                          className="h-4 w-4"
                        />
                      </td>
                      <td className="font-mono font-semibold">{it.barkod}</td>
                      <td className="max-w-md truncate" title={it.isim}>{it.isim || "-"}</td>
                      <td className="font-mono">{it.ebat || "-"}</td>
                      <td className="font-mono font-bold text-right">{it.adet}</td>
                      <td className="font-mono">{it.sehpa_konumu || "-"}</td>
                      <td className="text-xs">{it.urun_kalitesi || "-"}</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>

        <div className="p-4 border-t border-[color:var(--border)] flex flex-wrap items-center gap-3">
          <button
            data-testid="export-download-all"
            onClick={() => download("all")}
            disabled={downloading || !total}
            className="px-4 h-11 hard border-2 border-[color:var(--border)] hover:border-[color:var(--primary)] hover:text-[color:var(--primary)] font-bold uppercase tracking-wider text-sm flex items-center gap-2 disabled:opacity-40 transition-colors"
          >
            <Download size={14} /> Tümünü Aktar ({total ?? "-"})
          </button>
          <button
            data-testid="export-download-selected"
            onClick={() => download("selected")}
            disabled={downloading || selected.size === 0}
            className="ml-auto px-6 h-11 hard bg-[color:var(--success)] hover:opacity-90 text-white font-bold uppercase tracking-wider text-sm flex items-center gap-2 disabled:opacity-40"
          >
            {downloading ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Hazırlanıyor…
              </>
            ) : (
              <>
                <Download size={14} /> Seçilenleri Aktar ({selected.size})
              </>
            )}
          </button>
        </div>
      </div>

      {/* Persistent order list ("Sipariş Listesi") — survives filter changes so
          the user can build a single order/shipment from multiple sehpas. */}
      {selected.size > 0 && (
        <div
          data-testid="export-basket"
          className="hard border-2 border-[color:var(--success)] bg-[color:var(--success-bg)]"
        >
          <div className="p-4 border-b border-[color:var(--success)] flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2 text-[color:var(--success)]">
              <ClipboardList size={18} />
              <div
                className="font-black text-sm tracking-wider"
                style={{ textTransform: "none" }}
              >
                SİPARİŞ LİSTESİ · {orderLines.length} kalem · {totalBlok} blok
              </div>
            </div>
            <div className="text-xs text-[color:var(--text-2)]">
              {Object.keys(selectionByKonum).length} farklı konumdan
            </div>
            <button
              data-testid="export-basket-clear"
              onClick={clearSelection}
              className="ml-auto text-xs underline text-[color:var(--text-2)] hover:text-[color:var(--error)]"
            >
              tüm seçimi temizle
            </button>
          </div>

          <div className="p-3 flex flex-wrap gap-1.5">
            {Object.entries(selectionByKonum)
              .sort(([a], [b]) => naturalCompare(a, b))
              .map(([konum, g]) => (
                <span
                  key={konum}
                  data-testid="export-basket-chip"
                  className="inline-flex items-center gap-1 px-2 py-0.5 hard border border-[color:var(--success)] bg-white text-[11px] font-bold"
                >
                  <span className="font-mono">{konum}</span>
                  <span className="text-[color:var(--text-3)]">·</span>
                  <span className="text-[color:var(--success)]">{g.blok} blok</span>
                </span>
              ))}
          </div>

          <div className="max-h-72 overflow-auto border-t border-[color:var(--success)]">
            <table className="dense-table w-full">
              <thead className="sticky top-0 bg-[color:var(--success-bg)] z-10">
                <tr>
                  <th>Konum</th>
                  <th>Cam İsmi</th>
                  <th>Ölçü</th>
                  <th>Geliş Tarihi</th>
                  <th className="text-right">Blok</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {orderLines.map((g) => (
                  <tr key={g.key} data-testid="export-basket-row">
                    <td className="font-mono text-xs font-bold">{g.konum}</td>
                    <td className="max-w-md truncate" title={g.isim}>
                      {g.isim}
                    </td>
                    <td className="font-mono text-xs">{g.ebat}</td>
                    <td className="font-mono text-xs">
                      {g.gelis_tarihi || "-"}
                    </td>
                    <td className="font-mono font-black text-lg text-right text-[color:var(--success)]">
                      {g.blok}
                    </td>
                    <td>
                      <button
                        data-testid={`export-basket-remove-${g.key}`}
                        onClick={() => removeGroup(g.ids)}
                        title="Bu satırı sipariş listesinden çıkar"
                        className="w-6 h-6 flex items-center justify-center text-[color:var(--error)] hover:bg-[color:var(--error-bg)] hard border border-transparent hover:border-[color:var(--error)]"
                      >
                        <X size={12} strokeWidth={3} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Sipariş Fişi meta + oluştur */}
          <div className="p-4 border-t border-[color:var(--success)] bg-white grid gap-3 md:grid-cols-[1fr_1fr_auto] items-end">
            <div>
              <label className="ticker text-[10px] text-[color:var(--text-3)] block mb-1">
                MÜŞTERİ (opsiyonel)
              </label>
              <input
                data-testid="export-slip-musteri"
                value={musteri}
                onChange={(e) => setMusteri(e.target.value)}
                placeholder="örn. Yılmaz Ticaret"
                className="w-full h-10 px-3 hard border-2 border-[color:var(--border)] bg-white text-sm focus:border-[color:var(--success)] outline-none"
              />
            </div>
            <div>
              <label className="ticker text-[10px] text-[color:var(--text-3)] block mb-1">
                AÇIKLAMA / NOT
              </label>
              <input
                data-testid="export-slip-not"
                value={siparisNotu}
                onChange={(e) => setSiparisNotu(e.target.value)}
                placeholder="Sevkiyat tarihi, araç plakası vb."
                className="w-full h-10 px-3 hard border-2 border-[color:var(--border)] bg-white text-sm focus:border-[color:var(--success)] outline-none"
              />
            </div>
            <button
              data-testid="export-create-order-slip"
              onClick={createOrderSlip}
              disabled={creatingSlip || selected.size === 0}
              className="h-10 px-5 hard bg-[color:var(--success)] hover:opacity-90 text-white font-bold uppercase tracking-wider text-sm flex items-center gap-2 disabled:opacity-40"
            >
              {creatingSlip ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Hazırlanıyor…
                </>
              ) : (
                <>
                  <Receipt size={14} /> Sipariş Fişi Oluştur
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================ Gelen Sipariş ============================= */

/**
 * Paste a customer order (multi-line text like the one below) and the panel
 * fetches inventory matches per line so the operator can tick the exact
 * blocks to send. Ticked items are added to the parent's `selected` map —
 * the same order-list basket used elsewhere in this page.
 *
 *   6mm Sunguard SN 70/35 A GUARD (POL) 6000x3210 : 2 Blok (18 Temmuz)
 *   4mm Climaguard Premium 2 T GUARD (POL) 6000x3210 : 1 Blok (23 Temmuz)
 *   ...
 */
function IncomingOrderPanel({ onPickItem, selected }) {
  const [text, setText] = useState("");
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openIdx, setOpenIdx] = useState(null);

  const analyze = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/order/parse", { text });
      setLines(data.lines || []);
      // Expand the first line automatically for convenience
      setOpenIdx(0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const clear = () => {
    setText("");
    setLines([]);
    setOpenIdx(null);
  };

  const pickedCountForLine = (line) => {
    let c = 0;
    for (const m of line.matches || []) if (selected.has(m.id)) c += 1;
    return c;
  };

  return (
    <div
      data-testid="incoming-order-panel"
      className="hard border-2 border-[color:var(--primary)] bg-[color:var(--surface)]"
    >
      <div className="p-4 border-b border-[color:var(--border)] bg-[color:var(--bg)]">
        <div className="flex items-center gap-2">
          <ClipboardList size={18} className="text-[color:var(--primary)]" />
          <h2 className="text-lg font-black">Gelen Sipariş</h2>
        </div>
        <p className="text-xs text-[color:var(--text-2)] mt-1">
          Müşteriden gelen sipariş listesini aşağıya yapıştırın. Her satır için
          sistem stoktaki adresleri bulur; operatör tam ihtiyaç kadar blok
          seçince vinç sadece o blokları yükler.
        </p>
      </div>

      <div className="p-4 grid gap-3 md:grid-cols-[1fr_auto] items-start">
        <textarea
          data-testid="incoming-order-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={5}
          placeholder={"6mm Sunguard SN 70/35 A GUARD (POL) 6000x3210 : 2 Blok (18 Temmuz)\n4mm Climaguard Premium 2 T GUARD (POL) 6000x3210 : 1 Blok (23 Temmuz)"}
          className="w-full font-mono text-xs px-3 py-2 hard border-2 border-[color:var(--border)] bg-white focus:border-[color:var(--primary)] outline-none resize-y"
        />
        <div className="flex md:flex-col gap-2">
          <button
            data-testid="incoming-order-analyze"
            onClick={analyze}
            disabled={loading || !text.trim()}
            className="h-11 px-5 hard bg-[color:var(--primary)] text-white font-bold uppercase tracking-wider text-sm flex items-center gap-2 disabled:opacity-40"
          >
            {loading ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Analiz…
              </>
            ) : (
              <>
                <Search size={14} /> Analiz Et
              </>
            )}
          </button>
          {lines.length > 0 && (
            <button
              data-testid="incoming-order-clear"
              onClick={clear}
              className="h-11 px-4 hard border-2 border-[color:var(--border)] hover:border-[color:var(--error)] hover:text-[color:var(--error)] text-xs font-bold uppercase"
            >
              Temizle
            </button>
          )}
        </div>
      </div>

      {lines.length > 0 && (
        <ul
          data-testid="incoming-order-lines"
          className="divide-y divide-[color:var(--border)] border-t border-[color:var(--border)]"
        >
          {lines.map((line, i) => {
            const picked = pickedCountForLine(line);
            const complete = line.adet > 0 && picked >= line.adet;
            const partial = picked > 0 && picked < line.adet;
            const isOpen = openIdx === i;
            const status = complete
              ? "bg-[color:var(--success-bg)] text-[color:var(--success)] border-[color:var(--success)]"
              : partial
                ? "bg-[color:var(--warning-bg)] text-[color:var(--warning)] border-[color:var(--warning)]"
                : line.match_count === 0
                  ? "bg-[color:var(--error-bg)] text-[color:var(--error)] border-[color:var(--error)]"
                  : "bg-white text-[color:var(--text-2)] border-[color:var(--border)]";

            return (
              <li key={i} data-testid={`incoming-line-${i}`}>
                <button
                  onClick={() => setOpenIdx(isOpen ? null : i)}
                  className="w-full px-4 py-3 flex items-center gap-3 hover:bg-[color:var(--bg)] text-left"
                >
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-1 hard border-2 text-[10px] font-bold uppercase tracking-wider ${status}`}
                  >
                    {complete ? (
                      <CheckCircle2 size={11} />
                    ) : line.match_count === 0 ? (
                      <AlertTriangle size={11} />
                    ) : null}
                    {picked}/{line.adet || "?"} Blok
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">
                      {line.isim || <em className="text-[color:var(--text-3)]">isim çözülemedi</em>}
                    </div>
                    <div className="text-[11px] text-[color:var(--text-3)] mt-0.5 flex items-center gap-2">
                      {line.ebat && <span className="font-mono">{line.ebat}</span>}
                      {line.teslim_tarihi && (
                        <>
                          <span>·</span>
                          <span className="text-[color:var(--warning)]">
                            {line.teslim_tarihi}
                          </span>
                        </>
                      )}
                      <span>·</span>
                      <span>{line.match_count} eşleşme</span>
                    </div>
                  </div>
                  <ChevronDown
                    size={16}
                    className={`transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>

                {isOpen && (
                  <div className="border-t border-[color:var(--border)] bg-[color:var(--bg)]">
                    {line.match_count === 0 ? (
                      <div className="p-6 text-center text-sm text-[color:var(--error)]">
                        Bu cam için stoğunda eşleşme bulunamadı.
                      </div>
                    ) : (
                      <>
                        <div className="px-4 py-2 flex flex-wrap items-center gap-2 bg-[color:var(--bg)] border-b border-[color:var(--border)]">
                          <span className="ticker text-[10px] text-[color:var(--text-3)]">
                            FIFO — en eski geliş tarihi üstte
                          </span>
                          {line.adet > 0 && (
                            <button
                              data-testid={`incoming-auto-pick-${i}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                let need = line.adet - picked;
                                if (need <= 0) return;
                                for (const m of line.matches) {
                                  if (need <= 0) break;
                                  if (!selected.has(m.id)) {
                                    onPickItem(m);
                                    need -= 1;
                                  }
                                }
                              }}
                              className="ml-auto h-7 px-2 hard border border-[color:var(--primary)] text-[color:var(--primary)] hover:bg-[color:var(--primary)] hover:text-white text-[10px] font-bold uppercase tracking-wider"
                            >
                              En eskiden {line.adet - picked} blok otomatik seç
                            </button>
                          )}
                        </div>
                        <div className="max-h-60 overflow-auto">
                          <table className="dense-table w-full">
                            <thead className="sticky top-0 bg-[color:var(--bg)] z-10">
                              <tr>
                                <th className="w-8" />
                                <th>Konum</th>
                                <th>Geliş Tarihi</th>
                                <th>Barkod</th>
                                <th>İsim</th>
                                <th>Ebat</th>
                                <th className="text-right">Adet</th>
                              </tr>
                            </thead>
                            <tbody>
                              {line.matches.map((m, idx) => {
                                const isSel = selected.has(m.id);
                                const isOldest = idx < (line.adet || 1);
                                return (
                                  <tr
                                    key={m.id}
                                    data-testid={`incoming-match-${i}-${m.id}`}
                                    onClick={() => onPickItem(m)}
                                    className={`cursor-pointer ${
                                      isSel
                                        ? "bg-[color:var(--success-bg)]"
                                        : "hover:bg-white"
                                    }`}
                                  >
                                    <td onClick={(e) => e.stopPropagation()}>
                                      <input
                                        type="checkbox"
                                        checked={isSel}
                                        onChange={() => onPickItem(m)}
                                        className="h-4 w-4"
                                      />
                                    </td>
                                    <td className="font-mono font-bold text-xs">
                                      {m.sehpa_konumu || "-"}
                                    </td>
                                    <td className="text-xs font-mono">
                                      {m.gelis_tarihi ? (
                                        <span
                                          className={
                                            isOldest
                                              ? "inline-flex items-center gap-1 px-1.5 py-0.5 bg-[color:var(--warning-bg)] text-[color:var(--warning)] font-bold"
                                              : ""
                                          }
                                          title={
                                            isOldest ? "FIFO — bu blok gitmeli" : ""
                                          }
                                        >
                                          {isOldest && "⚠ "}
                                          {m.gelis_tarihi}
                                        </span>
                                      ) : (
                                        <span className="text-[color:var(--text-3)]">
                                          -
                                        </span>
                                      )}
                                    </td>
                                    <td className="font-mono text-xs">{m.barkod}</td>
                                    <td className="text-xs truncate max-w-xs" title={m.isim}>
                                      {m.isim}
                                    </td>
                                    <td className="font-mono text-xs">{m.ebat}</td>
                                    <td className="font-mono font-bold text-right">
                                      {m.adet}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}


function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
